import { getBusiness } from 'lib/api';
import { getIntegrationStatusByInternalName } from 'lib/api/integrations';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import EnhancedTab, { TabRow } from 'lib/designSystem/TabSimulation';
import {
  showAlert,
  validateCompanyTypes,
  validatePermissions,
} from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { AuthState } from '../../../services/redux/authSlice';
import BankAccounts from './components/BankAccounts';
import Client from './components/Client';
import ProfileCompliance from './components/Compliance';
import Contacts from './components/Contacts';
import Contracts from './components/Contracts';
import Files from './components/Files';
import OperativeProfile from './components/Operative';

interface Tab extends TabRow {
  path: string;
}
const Profile = () => {
  const { id, page } = useParams<{
    id: string;
    businessId: string;
    page: string;
  }>();
  const currentPath = window.location.pathname;
  const initialPath = currentPath.split('/')[2];
  const temporary = initialPath === 'temporary';
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [business, setBusiness] = useState({ name: '', companies: [] });
  const [companyTabs, setCompanyTabs] = useState<Tab[]>([]);
  const navigate = useNavigate();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [startValue, setStartValue] = useState(0);

  const tabs = (hasDS: boolean) => {
    return [
      {
        id: 1,
        label: 'Resumen',
        component: <Client showEnrolledPath />,
        path: 'summary',
      },
      ...(validatePermissions(user, 'business_can_view_files')
        ? [
            {
              id: 2,
              label: 'Archivos',
              component: <Files businessIdentifier={id} />,
              path: 'files',
            },
          ]
        : []),
      {
        id: 3,
        label: 'Contactos',
        component: <Contacts />,
        path: 'contacts',
      },
      ...(hasDS
        ? [
            {
              id: 5,
              label: 'Contratos',
              component: <Contracts />,
              path: 'contracts',
            },
          ]
        : []),
      {
        id: 4,
        label: 'Banco',
        component: <BankAccounts />,
        path: 'bankAccounts',
      },
      ...(validateCompanyTypes(user, 'FinancingHub')
        ? [
            {
              id: 5,
              label: 'Operativo',
              component: <OperativeProfile businessIdentifier={id ?? ''} />,
              path: 'operative',
            },
          ]
        : []),

      ...(validatePermissions(user, 'business_can_view_compliance_files')
        ? [
            {
              id: 6,
              label: 'Compliance',
              component: <ProfileCompliance />,
              path: 'compliance',
            },
          ]
        : []),
    ];
  };

  const handleNavigation = (index: number) => {
    navigate(`/companies/${id}/profile/${companyTabs[index].path}`);
  };

  const handleNavigationTemporary = (index: number) => {
    navigate(`/companies/temporary/${id}/profile/${companyTabs[index].path}`);
  };

  const mainBusiness = async () => {
    if (!id) {
      setLoading(false);
      return alert('Ha ocurrido un error');
    }
    const { status: hasDS } = await getIntegrationStatusByInternalName(
      dispatch,
      'digital-signature',
    );
    const business = await getBusiness(dispatch, id);
    const newProfileTabs = tabs(hasDS);
    let startValueDefault = 0;
    startValueDefault = newProfileTabs.findIndex((x) => x.path === page);
    setCompanyTabs(newProfileTabs);
    setStartValue(startValueDefault === -1 ? 0 : startValueDefault);
    setBusiness(business);
  };

  const setUp = async () => {
    setLoading(true);
    try {
      if (!id) return setLoading(false);
      await mainBusiness();
    } catch (error) {
      showAlert(dispatch, 'error', 'Ha ocurrido un error');
    }
    setLoading(false);
  };

  useEffect(() => {
    void setUp();
  }, []);

  if (loading) return <CenteredCircularProgress />;

  return (
    <div>
      <EnhancedTab
        tabType="secondary"
        startValue={startValue}
        title={temporary ? business.name : undefined}
        tabs={companyTabs}
        onTabChange={!temporary ? handleNavigation : handleNavigationTemporary}
      />
    </div>
  );
};

export default Profile;
